import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, finalize, takeUntil, timer } from 'rxjs';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import { RequestChatMessage } from './request-chat.model';
import { RequestChatService } from './request-chat.service';

/** Ogni quanto la chat rilegge la richiesta da sola (il backend non ha push). */
const CHAT_REFRESH_MS = 10000;

const STATUS_TO_READ = 'NUOVA';
const STATUS_ANSWERED = 'INVIATO CV';

/**
 * Chat di una richiesta CAREGIVER_CONSULT, mostrata nel tab "Registro" al posto degli eventi. Lo staff scrive
 * come 'familytutor' e, a differenza della Web App, può modificare ed eliminare qualsiasi messaggio. Il backend
 * non ha push né un GET dedicato: la chat rilegge la richiesta ogni 10 secondi (e con "Aggiorna") per vedere i
 * nuovi messaggi della persona.
 */
@Component({
  selector: 'app-request-chat',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './request-chat.component.html',
  styleUrl: './request-chat.component.scss',
})
export class RequestChatComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Input({ required: true }) requestId!: number;
  /** Chat già presente nella richiesta caricata dalla pagina. */
  @Input() initialMessages: RequestChatMessage[] | null | undefined;
  /** Stato della richiesta: rispondendo mentre è NUOVA passa a INVIATO CV (risposta da leggere per la persona). */
  @Input() status: string | null | undefined;
  /** Il nuovo stato, quando lo cambia la chat rispondendo o lo trova diverso rileggendo la richiesta. */
  @Output() statusChanged = new EventEmitter<string>();

  /** Stato noto della richiesta: parte da `status` e segue ogni lettura e ogni cambio fatto da qui. */
  private currentStatus: string | null | undefined;

  @ViewChild('chatList') private chatList?: ElementRef<HTMLElement>;
  private scrollToBottom = true;

  messages: RequestChatMessage[] = [];
  draft = new FormControl<string>('', { nonNullable: true });
  sending = false;
  refreshing = false;
  refreshFailed = false;

  /** Messaggio in modifica e relativo testo, null quando nessuno è aperto. */
  editingId: string | null = null;
  editControl = new FormControl<string>('', { nonNullable: true });
  editSaving = false;

  /** Messaggio per cui si sta chiedendo conferma di eliminazione. */
  deletingId: string | null = null;
  deleteBusy = false;

  private unsubscribe = new Subject<void>();

  constructor(
    private chatService: RequestChatService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    timer(CHAT_REFRESH_MS, CHAT_REFRESH_MS)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.autoRefresh());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialMessages']) {
      this.setMessages(this.initialMessages ?? []);
    }
    if (changes['status']) {
      this.currentStatus = this.status;
    }
  }

  ngAfterViewChecked(): void {
    if (this.scrollToBottom && this.chatList) {
      const el = this.chatList.nativeElement;
      el.scrollTop = el.scrollHeight;
      this.scrollToBottom = false;
    }
  }

  trackById(_: number, message: RequestChatMessage): string {
    return message.id;
  }

  /**
   * Aggiornamento periodico: salta i giri in cui l'operatore sta scrivendo, modificando o eliminando
   * (non gli si deve cambiare la lista sotto le mani), con la scheda in secondo piano o con un
   * aggiornamento già in corso. Un errore qui non mostra nulla: ritenta al giro dopo.
   */
  private autoRefresh(): void {
    if (
      document.hidden ||
      this.refreshing ||
      this.sending ||
      this.editingId !== null ||
      this.deletingId !== null
    ) {
      return;
    }
    this.chatService
      .getChat(this.requestId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: ({ messages, status }) => {
          if (this.editingId === null && this.deletingId === null && !this.sending) {
            this.setMessages(messages);
            this.followStatus(status);
          }
        },
        error: () => {},
      });
  }

  isFromUser(message: RequestChatMessage): boolean {
    return message.from === 'user';
  }

  authorLabel(message: RequestChatMessage): string {
    return this.isFromUser(message) ? 'Cliente' : 'Family Tutor';
  }

  send(): void {
    const message = this.draft.value.trim();
    if (!message || this.sending) {
      return;
    }
    this.sending = true;
    this.chatService
      .send(this.requestId, message)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.sending = false))
      )
      .subscribe({
        next: (sent) => {
          this.draft.reset('');
          this.setMessages([...this.messages, sent]);
          this.markAsAnswered();
        },
        error: () => this.toast.setAction("Errore durante l'invio del messaggio", false),
      });
  }

  /**
   * L'ultimo messaggio ora è dello staff: se la richiesta era NUOVA (da leggere) passa a INVIATO CV, e la
   * persona vede che c'è una risposta. Il messaggio è già partito: se il cambio stato fallisce lo si dice
   * all'operatore, che può correggerlo a mano dal selettore "Stato della richiesta".
   */
  private markAsAnswered(): void {
    if (this.currentStatus !== STATUS_TO_READ) {
      return;
    }
    this.chatService
      .setRequestStatus(this.requestId, STATUS_ANSWERED)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => this.followStatus(STATUS_ANSWERED),
        error: () =>
          this.toast.setAction(
            'Messaggio inviato, ma non è stato possibile portare la richiesta a INVIATO CV',
            false
          ),
      });
  }

  /** Allinea lo stato noto a quello della richiesta e lo comunica a chi lo mostra altrove nella pagina. */
  private followStatus(status: string | null | undefined): void {
    if (status && status !== this.currentStatus) {
      this.currentStatus = status;
      this.statusChanged.emit(status);
    }
  }

  /** Rilegge la richiesta e ne sostituisce i messaggi (una modifica o un'eliminazione aperta si chiude se il messaggio non c'è più). */
  refresh(): void {
    if (this.refreshing) {
      return;
    }
    this.refreshing = true;
    this.refreshFailed = false;
    this.chatService
      .getChat(this.requestId)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.refreshing = false))
      )
      .subscribe({
        next: ({ messages, status }) => {
          this.setMessages(messages);
          this.followStatus(status);
        },
        error: () => (this.refreshFailed = true),
      });
  }

  startEdit(message: RequestChatMessage): void {
    this.deletingId = null;
    this.editingId = message.id;
    this.editControl.setValue(message.message);
  }

  cancelEdit(): void {
    this.editingId = null;
  }

  saveEdit(message: RequestChatMessage): void {
    const text = this.editControl.value.trim();
    if (!text || this.editSaving) {
      return;
    }
    if (text === message.message) {
      this.cancelEdit();
      return;
    }
    this.editSaving = true;
    this.chatService
      .edit(this.requestId, message.id, text)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.editSaving = false))
      )
      .subscribe({
        next: () => {
          this.messages = this.messages.map((m) => (m.id === message.id ? { ...m, message: text } : m));
          this.editingId = null;
          this.toast.setAction('Messaggio modificato', true);
        },
        error: () => this.toast.setAction('Errore durante la modifica del messaggio', false),
      });
  }

  askDelete(message: RequestChatMessage): void {
    this.cancelEdit();
    this.deletingId = message.id;
  }

  cancelDelete(): void {
    this.deletingId = null;
  }

  confirmDelete(message: RequestChatMessage): void {
    if (this.deleteBusy) {
      return;
    }
    this.deleteBusy = true;
    this.chatService
      .delete(this.requestId, message.id)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.deleteBusy = false))
      )
      .subscribe({
        next: () => {
          this.messages = this.messages.filter((m) => m.id !== message.id);
          this.deletingId = null;
          this.toast.setAction('Messaggio eliminato', true);
        },
        error: () => this.toast.setAction("Errore durante l'eliminazione del messaggio", false),
      });
  }

  /** Dal più vecchio al più recente. */
  private setMessages(messages: RequestChatMessage[]): void {
    const sorted = [...messages].sort(
      (a, b) => new Date(a.sentOn).getTime() - new Date(b.sentOn).getTime()
    );
    // Scorre in fondo solo se in coda c'è qualcosa di nuovo, per non strappare chi sta rileggendo.
    this.scrollToBottom =
      this.messages[this.messages.length - 1]?.id !== sorted[sorted.length - 1]?.id;
    this.messages = sorted;
    if (this.editingId && !this.messages.some((m) => m.id === this.editingId)) {
      this.editingId = null;
    }
    if (this.deletingId && !this.messages.some((m) => m.id === this.deletingId)) {
      this.deletingId = null;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
